import type { ReactNode } from 'react';
import { useEffect, useId, useRef } from 'react';

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // `onClose` biasanya fungsi baru di setiap render pemanggil; disimpan di ref agar efek fokus di bawah
  // hanya berjalan saat dialog dibuka/ditutup (bukan di setiap ketikan, yang akan menarik fokus dari kolom isian).
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    // fokus ke dialog bila belum ada elemen di dalamnya yang terfokus (mis. autoFocus)
    if (box.current && !box.current.contains(document.activeElement)) box.current.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return closeRef.current();
      if (e.key !== 'Tab' || !box.current) return;
      // jebak Tab di dalam dialog
      const items = [...box.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (items.length === 0) return e.preventDefault();
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === box.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      previous?.focus?.(); // kembalikan fokus ke pemicu saat dialog ditutup
    };
  }, [open]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeRef.current();
      }}
    >
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-wds-size="md"
        className="wds-dialog max-h-[90vh] w-full max-w-lg overflow-auto outline-none"
      >
        <div className="wds-dialog__header">
          <div className="wds-dialog__heading">
            <h2 id={titleId} className="wds-dialog__title">
              {title}
            </h2>
          </div>
        </div>
        <div className="wds-dialog__content">{children}</div>
      </div>
    </div>
  );
}
